/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { searchMembers } from "apis/members";
import { THost } from "apis/visitors/setting/type";
import { useEffect, useState } from "react";
import Modal from "react-modal";
import { theme } from "styles/theme";
import { TMember } from "types/user.types";
import Button from "uis/Button";
import Icon from "uis/Icon";
import InputField from "uis/InputField";
import SearchManagerItem from "uis/layout/search-manager-item";
import UI from "@mile-meeting/mile-ui";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "550px",
    borderRadius: "20px",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  registeredManagers?: THost[];
  onUpdate?: (value: TMember[]) => Promise<void> | void;
};

const Title = styled.p`
  font-size: 20px;
  font-weight: 400;
`;

export function AddManagerModal(props: Props) {
  const { modalIsOpen, closeModal, registeredManagers, onUpdate = () => {} } = props;

  const [members, setMembers] = useState<TMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<TMember[]>([]);
  const [registeredEmailList, setRegisteredEmailList] = useState<string[]>([]);

  useEffect(() => {
    setSelectedMembers([]);
    setMembers([]);
    if (registeredManagers) setRegisteredEmailList([...registeredManagers].map((item) => item.email));
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <UI.IconButton.Normal
          type="primary"
          icon="x"
          size={20}
          color="var(--alias-color-label-normal)"
          onClick={closeModal}
        />
      </div>
      <div
        css={css`
          display: flex;
          flex: 1;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          margin: 30px 10px;
        `}
      >
        <Title>추가할 담당자를 선택해주세요.</Title>
        <InputField
          placeholder="이름, 이메일 검색"
          onChangeDebounce={async (value) => {
            const res = await searchMembers(value);
            if (res.success) {
              setMembers(res.data ?? []);
            }
          }}
        />
        {selectedMembers.length > 0 ? (
          <div
            css={css`
              display: flex;
              width: 100%;
              overflow: auto;
              gap: 8px;
            `}
          >
            {selectedMembers.map((item, index) => {
              return (
                <Button
                  key={index}
                  onClick={() => {
                    setSelectedMembers((prev) => [...prev].filter((it) => it !== item));
                  }}
                  title={item.nickname}
                  type="secondary"
                  rightIcon={({ color }) => <Icon name="close" color={color} width={24} height={24} />}
                  styles={{
                    container: css`
                      gap: 8px;
                      padding: 12px;
                      border-radius: 100px;
                    `,
                    text: css`
                      white-space: nowrap;
                    `,
                  }}
                />
              );
            })}
          </div>
        ) : null}

        <div
          css={css`
            max-height: 400px;
            overflow: auto;
            width: 100%;
          `}
        >
          {[...members].map((item, index) => {
            return (
              <div
                css={css`
                  position: relative;
                `}
              >
                <SearchManagerItem
                  key={index}
                  member={item}
                  onClick={() => {
                    if (!item.isSlackConnected) return;
                    if (!registeredEmailList.includes(item.email)) {
                      if (![...selectedMembers].map((item) => item.email).includes(item.email)) {
                        setSelectedMembers((prev) => [...prev, item]);
                      } else {
                        setSelectedMembers((prev) => [...prev].filter((it) => it !== item));
                      }
                    }
                  }}
                  styles={{
                    container: css`
                      width: calc(100% - 48px);
                      border: none;
                      background-color: ${[...selectedMembers].map((item) => item.email).includes(item.email) ||
                      registeredEmailList.includes(item.email)
                        ? theme.neutral50
                        : ""};
                      &:hover {
                        background-color: ${theme.neutral50};
                      }
                    `,
                  }}
                  isDisabled={!item.isSlackConnected}
                  rightRender={() => {
                    if (registeredEmailList.includes(item.email)) {
                      return (
                        <p
                          css={css`
                            color: ${theme.primary500};
                            font-size: 14px;
                            font-weight: 500;
                            margin-left: auto;
                          `}
                        >
                          이미 추가된 멤버입니다.
                        </p>
                      );
                    } else if (!item.isSlackConnected) {
                      return (
                        <p
                          css={css`
                            color: ${theme.font.destructive};
                            font-size: 12px;
                            font-weight: 500;
                            margin-left: auto;
                          `}
                        >
                          미연동
                        </p>
                      );
                    }

                    return null;
                  }}
                />
              </div>
            );
          })}
        </div>

        <Button
          onClick={async () => {
            onUpdate(selectedMembers);
            closeModal();
          }}
          title="추가하기"
          styles={{
            container: css`
              width: 100%;
              padding: 16px 0;
            `,
          }}
        />
      </div>
    </Modal>
  );
}
